import { campusMilkTotal } from '@/lib/chefs/milk'
import { mergeDeliverablePacking } from '@/lib/chefs/packing'
import { mergeSupplyLines } from '@/lib/chefs/service-materials'
import type { Campus, Deliverable, MealtimeBlock, ProductType } from '@/lib/chefs/types'
import { PRODUCT_TYPE_LABEL } from '@/lib/chefs/types'

export interface TotalsBucket {
  productType: ProductType
  label: string
  qty: number
}

export interface CampusTotals {
  total: number
  buckets: TotalsBucket[]
}

const BUCKET_ORDER: ProductType[] = ['main', 'side', 'salad', 'beverage', 'baked', 'shelf', 'dessert', 'add-on']

export function computeCampusTotals(campus: Campus): CampusTotals {
  const buckets = new Map<ProductType, number>()
  let total = 0
  for (const block of campus.mealtimes) {
    for (const d of block.deliverables) {
      total += d.qty
      buckets.set(d.productType, (buckets.get(d.productType) ?? 0) + d.qty)
    }
  }
  // Milk left the mealtime rows for its own card (PROD-1502); it still counts as a beverage.
  const milk = campusMilkTotal(campus)
  if (milk > 0) {
    total += milk
    buckets.set('beverage', (buckets.get('beverage') ?? 0) + milk)
  }
  const ordered: TotalsBucket[] = BUCKET_ORDER.filter((pt) => buckets.has(pt)).map((pt) => ({
    productType: pt,
    label: PRODUCT_TYPE_LABEL[pt],
    qty: buckets.get(pt) ?? 0,
  }))
  return { total, buckets: ordered }
}

/**
 * TRUE MEAL COUNT for a mealtime block — one per bundle ordered, plus
 * standalone main entrées (a solo milk or condiment is not a meal).
 *
 * The tally lives in `shipmentsToCampuses`, which computes it per shipment
 * from the raw lines. It deliberately does NOT live here: identical packages
 * merge across bundles and service dates before a `Deliverable` exists, so the
 * rendered rows can no longer say which bundles or which days they came from.
 * Deriving the count from them read a 205-meal lunch as 210 — or 405, since
 * the answer depended on the order Postgres happened to return the lines in.
 */
export function computeMealtimeMeals(block: MealtimeBlock): number {
  return block.meals
}

/** Campus-wide true meal count — see `computeMealtimeMeals`. */
export function computeCampusMeals(campus: Campus): number {
  return campus.mealtimes.reduce((sum, block) => sum + computeMealtimeMeals(block), 0)
}

/**
 * True meal count summed across every campus — the day/week production
 * total Ops previously added up school-by-school (Yanping Ren). Same
 * per-shipment tally as `computeCampusMeals`, never re-derived from the
 * merged deliverable rows.
 */
export function computeTotalMeals(campuses: ReadonlyArray<Campus>): number {
  return campuses.reduce((sum, campus) => sum + computeCampusMeals(campus), 0)
}

/**
 * One aggregated mealtime view across EVERY campus: identical deliverables
 * (same name, serving, unit, temperature, bulk mode) merge with summed quantities so
 * the chef reads ONE production line per thing they make, regardless of how
 * many schools ordered it. Powers the "All schools" production card above
 * the per-school cards (Yanping Ren, Ops).
 *
 * Components ride from the first instance (identical packages carry the
 * same recipes). Packing (PROD-1500) never re-plans: each school's pans are
 * packed for that school, and the merged row pools them, fullest first
 * (`mergeDeliverablePacking`). A school whose row carries servings no spec
 * covers (`packingIncomplete`) leaves the merged row with no containers at
 * all, rather than pans covering only part of its quantity (D23).
 */
export function mergeCampusesMealtimes(campuses: ReadonlyArray<Campus>): MealtimeBlock[] {
  const byMealtime = new Map<string, { meals: number; rows: Map<string, Deliverable> }>()
  for (const campus of campuses) {
    for (const block of campus.mealtimes) {
      const bucket = byMealtime.get(block.mealtime) ?? { meals: 0, rows: new Map<string, Deliverable>() }
      bucket.meals += block.meals
      for (const d of block.deliverables) {
        // The serving joins the key because the merged row keeps the first
        // instance's components and serving: one school's 1/2 Cup mac and
        // another's 1 Cup mac are two things to pack, not one (PROD-1372).
        const key = `${d.name}|${d.servingSize ?? ''}|${d.unit}|${d.temperature}|${d.bulk ? 'bulk' : 'plated'}`
        const prior = bucket.rows.get(key)
        if (!prior) {
          // Cross-school production line: the order-group section (PROD-1296)
          // is a per-campus packing fact and does not survive the merge.
          const { groupKey: _groupKey, groupLabel: _groupLabel, ...rest } = d
          bucket.rows.set(key, { ...rest, id: `agg-${block.mealtime}-${key}` })
          continue
        }
        prior.qty += d.qty
        if (d.supplies && d.supplies.length > 0) {
          prior.supplies = mergeSupplyLines(prior.supplies ?? [], d.supplies)
        }
        // A row counted in another row's containers ("packed with") neither
        // adds to nor erases a plan (OLHSA's 300 owner row + its 25 "packed
        // with" row merge to the owner's 17 pans, the self-reference dropped);
        // a row with servings no spec covers clears the merged row's containers.
        const merged = mergeDeliverablePacking(prior, d, prior.name)
        delete prior.packing
        delete prior.packedWith
        delete prior.packingIncomplete
        Object.assign(prior, merged)
      }
      byMealtime.set(block.mealtime, bucket)
    }
  }
  return [...byMealtime.entries()].map(([mealtime, bucket]) => ({
    mealtime: mealtime as MealtimeBlock['mealtime'],
    meals: bucket.meals,
    deliverables: [...bucket.rows.values()],
  }))
}

/**
 * Serving count summed across campuses' deliverables — the print packet's
 * "Total servings" stat (kept as the print-side vocabulary; the on-screen
 * bubble speaks in MEALS via `computeTotalMeals`).
 */
export function countCampusesServings(campuses: ReadonlyArray<Campus>): number {
  let total = 0
  for (const campus of campuses) {
    for (const block of campus.mealtimes) {
      for (const d of block.deliverables) {
        total += d.qty
      }
    }
    // Milk rides on its own campus card (PROD-1502), not in the mealtimes.
    total += campusMilkTotal(campus)
  }
  return total
}

export function deliverableTemperatureRank(d: Deliverable): number {
  return d.temperature === 'hot' ? 0 : 1
}

export function sortDeliverables(deliverables: Deliverable[]): Deliverable[] {
  return [...deliverables].sort((a, b) => {
    const tempDiff = deliverableTemperatureRank(a) - deliverableTemperatureRank(b)
    if (tempDiff !== 0) {
      return tempDiff
    }
    return a.name.localeCompare(b.name)
  })
}
